/**
 * Estimate how many days of therapy a vial covers based on its matched peptide's
 * default dose + interval. Returns { days, mgPerDay, doseMg, dosesPerDay,
 * pctRemaining, totalDays } or null if the peptide/unit isn't compatible.
 */
export function computeDaysLeft(vial, peptide) {
  if (!peptide) return null;
  const dose = Number(peptide.default_dose);
  const interval = Number(peptide.default_interval_hours) || 24;
  const unit = (peptide.default_unit || "").toLowerCase();
  if (!dose || dose <= 0 || interval <= 0) return null;
  if (unit !== "mg" && unit !== "mcg") return null;
  const doseMg = unit === "mg" ? dose : dose / 1000;
  const dosesPerDay = 24 / interval;
  const mgPerDay = doseMg * dosesPerDay;
  if (mgPerDay <= 0) return null;
  const remaining = Number(vial.remaining_mg ?? vial.amount_mg) || 0;
  const perVial = Number(vial.amount_mg) || 0;
  const quantity = Math.max(1, Number(vial.quantity) || 1);
  // Total supply on hand = active vial remaining + sealed vials (each amount_mg).
  const totalOnHand = remaining + Math.max(0, quantity - 1) * perVial;
  const days = totalOnHand / mgPerDay;
  const totalDays = perVial > 0 ? (perVial * quantity) / mgPerDay : days;
  const pctRemaining = perVial > 0
    ? Math.max(0, Math.min(100, (totalOnHand / (perVial * quantity)) * 100))
    : 100;
  return { days, mgPerDay, doseMg, dosesPerDay, pctRemaining, totalDays };
}

export function formatDaysLeft(d) {
  if (d < 0.5) return "<½ day";
  if (d < 1) return "~½ day";
  if (d < 2) return "1 day";
  if (d < 60) return `${Math.round(d)} days`;
  return `${(d / 30).toFixed(1)} mo`;
}

/**
 * Peptide-name → peptide object index (case-insensitive).
 */
export function indexPeptidesByName(peptides) {
  const idx = new Map();
  for (const p of peptides || []) {
    if (p?.name) idx.set(p.name.toLowerCase(), p);
  }
  return idx;
}

/**
 * Return vials that need restocking soon.
 * Threshold in days is inclusive: vials with days_left <= threshold OR remaining_mg === 0.
 * Sorted ascending (most urgent first).
 */
export function getRestockAlerts(vials, peptides, thresholdDays = 7) {
  const idx = indexPeptidesByName(peptides);
  const rows = [];
  for (const v of vials || []) {
    const pep = idx.get((v.peptide_name || "").toLowerCase());
    const forecast = computeDaysLeft(v, pep);
    const remaining = Number(v.remaining_mg ?? v.amount_mg) || 0;
    if (!forecast) {
      // No forecast: still surface empty vials
      if (remaining <= 0) rows.push({ vial: v, forecast: null, days: 0 });
      continue;
    }
    if (forecast.days <= thresholdDays) rows.push({ vial: v, forecast, days: forecast.days });
  }
  rows.sort((a, b) => a.days - b.days);
  return rows;
}
